/**
 * Premium "Partner Lineup" — kategorie header + 3-5 partner cards.
 *
 * Each item is encoded as `Name|City|status` (status = active | coming).
 * Logos can be supplied via `media[]` aligned by index. The header pulses
 * an Allyver eyebrow + headline; cards drift in from below in a tight
 * stagger; status pills (LIVE / BALD) flicker on at the end.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine, makeTl,
  pickMediaSrc, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

type ParsedPartner = { name: string; city: string; status: "active" | "coming" };

function parseItem(raw: string): ParsedPartner {
  const parts = raw.split("|").map((p) => p.trim());
  const status = (parts[2] ?? "active").toLowerCase() === "coming" ? "coming" : "active";
  return { name: parts[0] ?? "", city: parts[1] ?? "", status };
}

export const partnerCategoryLineup: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = (inputs.text ?? "Unsere neuen Partner").trim();
  const sub = (inputs.subtext ?? "Frisch auf Allyver registriert").trim();
  const items = (inputs.items ?? [
    "JUSATECH|Hausen ob Verena|active",
    "Dekomi|Villingen-Schw.|active",
    "Seedos GmbH|Hausen ob Verena|active",
    "BENHAUSER|Dauchingen|coming",
  ]).slice(0, 5).map(parseItem);

  const headlineSize = typeSize(isVertical ? "lg" : "md", ctx.format) * 0.78;
  const subSize = typeSize("sm", ctx.format) * 0.85;
  const eyeSize = typeSize("xs", ctx.format) * 0.95;
  const cardNameSize = typeSize(isVertical ? "md" : "sm", ctx.format) * 0.78;
  const cardCitySize = typeSize("sm", ctx.format) * 0.7;
  const pillSize = typeSize("xs", ctx.format) * 0.78;
  const logoTileSize = isVertical ? 92 : 76;

  const t = makeTl(clipStartSec);
  const eyeId = `${clipPrefix}-eye`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const ulId = `${clipPrefix}-ul`;
  const cardIds = items.map((_, i) => `${clipPrefix}-card-${i}`);
  const pillIds = items.map((_, i) => `${clipPrefix}-pill-${i}`);
  const footerId = `${clipPrefix}-foot`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const headerHtml = `<div style="display:flex;flex-direction:column;align-items:center;gap:14px;text-align:center;">
    <div id="${eyeId}" style="display:flex;align-items:center;gap:14px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize}px;letter-spacing:7px;text-transform:uppercase;color:${accent};">
      <span style="width:30px;height:2px;background:${accent};"></span>
      <span>Allyver Partner Showcase</span>
      <span style="width:30px;height:2px;background:${accent};"></span>
    </div>
    <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headlineSize}px;line-height:1;letter-spacing:-2px;color:${themeColors.fg};text-wrap:balance;max-width:${isVertical ? 880 : 1300}px;text-shadow:0 0 50px ${withAlpha(accent, 0.35)};">${escapeHtml(headline)}</div>
    <div id="${ulId}" style="height:2px;width:0;background:${accent};box-shadow:0 0 14px ${withAlpha(accent, 0.7)};"></div>
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;color:${withAlpha(themeColors.fg, 0.78)};max-width:${isVertical ? 760 : 1000}px;">${escapeHtml(sub)}</div>` : ""}
  </div>`;

  const cardsHtml = `<div style="display:flex;flex-direction:column;gap:12px;width:100%;max-width:${isVertical ? 880 : 1180}px;">
    ${items.map((item, i) => {
      const logoUrl = pickMediaSrc(inputs.media, i);
      const isActive = item.status === "active";
      const statusBg = isActive ? withAlpha("#22c55e", 0.18) : withAlpha(accent, 0.18);
      const statusBorder = isActive ? withAlpha("#22c55e", 0.55) : withAlpha(accent, 0.55);
      const statusColor = isActive ? "#4ade80" : accent;
      const statusDot = isActive ? "#22c55e" : accent;
      const statusLabel = isActive ? "LIVE" : "BALD";

      const logoInner = logoUrl
        ? `<img src="${escapeHtml(logoUrl)}" alt="${escapeHtml(item.name)}" style="width:100%;height:100%;object-fit:contain;padding:8px;" />`
        : `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${logoTileSize * 0.42}px;color:${accent};">${escapeHtml(item.name.charAt(0))}</div>`;

      return `<div id="${cardIds[i]}" style="display:flex;align-items:center;gap:18px;padding:14px 18px;border-radius:22px;background:linear-gradient(180deg, ${withAlpha(themeColors.fg, 0.07)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%);border:1.5px solid ${withAlpha(themeColors.fg, 0.16)};box-shadow:0 16px 38px rgba(0,0,0,0.32);">
        <div style="width:${logoTileSize}px;height:${logoTileSize}px;min-width:${logoTileSize}px;border-radius:18px;background:${withAlpha(themeColors.fg, 0.05)};border:1px solid ${withAlpha(themeColors.fg, 0.12)};display:flex;align-items:center;justify-content:center;overflow:hidden;">${logoInner}</div>
        <div style="flex:1;display:flex;flex-direction:column;gap:4px;min-width:0;">
          <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${cardNameSize}px;line-height:1;letter-spacing:-1px;color:${themeColors.fg};">${escapeHtml(item.name)}</div>
          <div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${cardCitySize}px;color:${withAlpha(themeColors.fg, 0.65)};display:flex;align-items:center;gap:6px;">
            <svg width="${Math.round(cardCitySize)}" height="${Math.round(cardCitySize)}" viewBox="0 0 24 24" fill="none" stroke="${withAlpha(themeColors.fg, 0.6)}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            <span>${escapeHtml(item.city)}</span>
          </div>
        </div>
        <div id="${pillIds[i]}" style="display:inline-flex;align-items:center;gap:8px;padding:8px 14px;border-radius:9999px;background:${statusBg};border:1.5px solid ${statusBorder};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${pillSize}px;letter-spacing:2px;text-transform:uppercase;color:${statusColor};white-space:nowrap;">
          <span style="width:8px;height:8px;border-radius:9999px;background:${statusDot};box-shadow:0 0 10px ${statusDot};"></span>
          <span>${statusLabel}</span>
        </div>
      </div>`;
    }).join("")}
  </div>`;

  const footerHtml = `<div id="${footerId}" style="display:inline-flex;align-items:center;gap:10px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize * 0.95}px;letter-spacing:5px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.7)};">
    <span style="width:28px;height:2px;background:${accent};"></span>
    <span>Verfügbar auf Allyver</span>
    <span style="width:28px;height:2px;background:${accent};"></span>
  </div>`;

  const padTopBot = isVertical ? 80 : 56;
  const padX = isVertical ? 60 : 80;
  const html = `<div id="${clipPrefix}-pad" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 32 : 24}px;padding:${padTopBot}px ${padX}px;">
    ${headerHtml}
    ${cardsHtml}
    ${footerHtml}
  </div>`;

  const init = [
    gsapSetLine(`#${eyeId}`, { opacity: 0, y: -16 }),
    gsapSetLine(`#${headId}`, { opacity: 0, y: 24 }),
    gsapSetLine(`#${ulId}`, { width: 0 }),
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 14 }) : "",
    ...cardIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 50 })),
    ...pillIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, scale: 0.6 })),
    gsapSetLine(`#${footerId}`, { opacity: 0, y: 12 }),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.05),
    t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power3.out\"" }, 0.2),
    t.to(`#${ulId}`, { width: 80, duration: 0.45, ease: "raw:\"power2.out\"" }, 0.55),
  ];
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.7));

  cardIds.forEach((id, i) => {
    tweens.push(t.to(`#${id}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 0.95 + i * 0.16));
  });
  pillIds.forEach((id, i) => {
    tweens.push(t.to(`#${id}`, { opacity: 1, scale: 1, duration: 0.4, ease: "raw:\"back.out(2.0)\"" }, 1.15 + i * 0.16));
  });

  const footerAt = 1.15 + items.length * 0.16 + 0.2;
  tweens.push(t.to(`#${footerId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, footerAt));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
